import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DateRange } from 'react-day-picker';

interface IncidentsFiltersState {
  external_code: string;
  status: string[];
  report_date: DateRange | undefined;
  resolution_date: DateRange | undefined;
  shop: string[];
  team: string[];
  courier: string[];
}

interface UseIncidentsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

interface LoadOptionsResponse {
  options: { value: string; label: string }[];
  hasMore: boolean;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useIncidentsFilters = ({ initialConditions = [], onChange }: UseIncidentsFiltersProps) => {
  const httpClient = createHttpClient(apiClientWH);
  const { getTeamType } = usePermissions();
  const is3pl = getTeamType() === 'tenant_3pl';

  const [filters, setFilters] = useState<IncidentsFiltersState>(() => parseConditionsToState(initialConditions));

  const { data: courierOptions = [], isLoading: isLoadingCouriers } = useQuery({
    queryKey: ['incident-filter-couriers'],
    queryFn: async () => {
      const response = await httpClient.get<any>('/couriers');
      const list = Array.isArray(response) ? response : (response.data ?? []);
      return list.map((c: any) => ({ value: String(c.id), label: c.name }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: teamOptions = [], isLoading: isLoadingTeams } = useQuery({
    queryKey: ['incident-filter-teams'],
    queryFn: async () => {
      const response = await httpClient.get<any>('/teams-child', { per_page: 200 });
      return (response.data ?? []).map((t: any) => ({ value: String(t.id), label: t.name }));
    },
    staleTime: 5 * 60 * 1000,
    enabled: is3pl,
  });

  const loadShopsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const client = createHttpClient(apiClientWH);
        const queryParams: Record<string, any> = { page: params.page, per_page: params.limit };
        if (params.search) queryParams.name = params.search;
        const response = await client.get<any>('/shops', queryParams);
        const list = Array.isArray(response) ? response : (response.data ?? []);
        const meta = response.meta;
        const options = list.map((s: any) => ({ value: String(s.id), label: s.name }));
        const currentPage = meta?.current_page ?? meta?.currentPage ?? 1;
        const lastPage = meta?.last_page ?? meta?.lastPage ?? 1;
        return { options, hasMore: currentPage < lastPage };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [],
  );

  const convertStateToConditions = useCallback((state: IncidentsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.external_code?.trim()) {
      conditions.push({
        column: 'external_code',
        action: 'contains',
        value: state.external_code.trim(),
      });
    }

    if (state.status.length > 0) {
      conditions.push({
        column: 'status',
        action: 'in',
        value: state.status,
      });
    }

    if (state.shop.length > 0) {
      conditions.push({
        column: 'shop_id',
        action: 'in',
        value: state.shop,
      });
    }

    if (state.team.length > 0) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: state.team,
      });
    }

    if (state.courier.length > 0) {
      conditions.push({
        column: 'courier_id',
        action: 'in',
        value: state.courier,
      });
    }

    if (state.report_date?.from && state.report_date?.to) {
      conditions.push({
        column: 'report_date',
        action: 'greater_equal',
        value: formatDateForAPI(state.report_date.from),
      });
      conditions.push({
        column: 'report_date',
        action: 'less_equal',
        value: formatDateForAPI(state.report_date.to),
      });
    }

    if (state.resolution_date?.from && state.resolution_date?.to) {
      conditions.push({
        column: 'resolution_date',
        action: 'greater_equal',
        value: formatDateForAPI(state.resolution_date.from),
      });
      conditions.push({
        column: 'resolution_date',
        action: 'less_equal',
        value: formatDateForAPI(state.resolution_date.to),
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<IncidentsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleExternalCodeChange = useCallback(
    (value: string) => updateFilters({ external_code: value }),
    [updateFilters],
  );
  const handleStatusChange = useCallback((values: string[]) => updateFilters({ status: values }), [updateFilters]);
  const handleShopChange = useCallback((values: string[]) => updateFilters({ shop: values }), [updateFilters]);
  const handleTeamChange = useCallback((values: string[]) => updateFilters({ team: values }), [updateFilters]);
  const handleCourierChange = useCallback((values: string[]) => updateFilters({ courier: values }), [updateFilters]);
  const handleReportDateChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ report_date: range }),
    [updateFilters],
  );
  const handleResolutionDateChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ resolution_date: range }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: IncidentsFiltersState = {
      external_code: '',
      status: [],
      report_date: undefined,
      resolution_date: undefined,
      shop: [],
      team: [],
      courier: [],
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    is3pl,
    courierOptions,
    teamOptions,
    isLoadingCouriers,
    isLoadingTeams,
    loadShopsForMultiSelect,
    handleExternalCodeChange,
    handleStatusChange,
    handleShopChange,
    handleTeamChange,
    handleCourierChange,
    handleReportDateChange,
    handleResolutionDateChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): IncidentsFiltersState {
  const state: IncidentsFiltersState = {
    external_code: '',
    status: [],
    report_date: undefined,
    resolution_date: undefined,
    shop: [],
    team: [],
    courier: [],
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const externalCodeCond = byColumn['external_code']?.find((c) => c.action === 'contains');
  if (externalCodeCond) state.external_code = externalCodeCond.value as string;

  const getArrayValue = (column: string): string[] => {
    const cond = byColumn[column]?.find((c) => c.action === 'in');
    if (!cond) return [];
    return Array.isArray(cond.value) ? cond.value.map(String) : [String(cond.value)];
  };

  state.status = getArrayValue('status');
  state.shop = getArrayValue('shop_id');
  state.team = getArrayValue('team_id');
  state.courier = getArrayValue('courier_id');

  const parseDateRange = (column: string): DateRange | undefined => {
    const arr = byColumn[column] || [];
    const from = arr.find((c) => c.action === 'greater_equal');
    const to = arr.find((c) => c.action === 'less_equal');
    if (from?.value && to?.value) {
      return {
        from: new Date(from.value as string),
        to: new Date(to.value as string),
      };
    }
    return undefined;
  };

  state.report_date = parseDateRange('report_date');
  state.resolution_date = parseDateRange('resolution_date');

  return state;
}
